import { useEffect, useMemo } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, Form, Input, InputNumber, Select } from 'antd'
import { ROOM_STATUS_OPTIONS, ROOM_TYPE_OPTIONS } from '@/features/master-data/const/room-editor-modal.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'

export type RoomType = 'office' | 'workstation' | 'warehouse' | 'other'
export type RoomStatus = 'vacant' | 'reserved' | 'occupied' | 'maintenance' | 'disabled'

export type RoomEditorRecord = {
  id: number
  siteId: number
  site: { id: number; name: string }
  roomNo: string
  floor: string | null
  roomType: RoomType
  area: string | null
  status: RoomStatus
  note: string | null
}

export type RoomFormValues = {
  siteId: number
  roomNo: string
  floor?: string
  roomType: RoomType
  area?: number
  status: RoomStatus
  note?: string
}

export function RoomEditorModal({
  defaultSiteId,
  editing,
  onClose,
  onSaved,
  siteLocked = false,
  siteOptions
}: {
  defaultSiteId?: number
  editing?: RoomEditorRecord | 'new'
  onClose: () => void
  onSaved: () => Promise<void>
  siteLocked?: boolean
  siteOptions: Array<{ label: string; value: number }>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<RoomFormValues>()
  const upsert = useMutation(trpc.room.upsert.mutationOptions())
  const initialValues = useMemo<Partial<RoomFormValues>>(
    () =>
      editing && editing !== 'new'
        ? {
            area: editing.area === null ? undefined : Number(editing.area),
            floor: editing.floor ?? undefined,
            note: editing.note ?? undefined,
            roomNo: editing.roomNo,
            roomType: editing.roomType,
            siteId: editing.siteId,
            status: editing.status
          }
        : { floor: '', note: '', roomNo: '', roomType: 'office', siteId: defaultSiteId, status: 'vacant' },
    [defaultSiteId, editing]
  )
  const statusOptions =
    editing !== 'new' && editing?.status === 'maintenance'
      ? [{ label: '维修中（历史状态）', value: 'maintenance' as const }, ...ROOM_STATUS_OPTIONS]
      : ROOM_STATUS_OPTIONS

  useEffect(() => {
    if (!editing) {
      return
    }
    form.resetFields()
    form.setFieldsValue(initialValues)
  }, [editing, form, initialValues])

  const save = async (values: RoomFormValues) => {
    try {
      await upsert.mutateAsync({
        id: editing === 'new' ? undefined : editing?.id,
        data: {
          area: values.area ?? null,
          floor: values.floor?.trim() || null,
          note: values.note?.trim() || null,
          roomNo: values.roomNo.trim(),
          roomType: values.roomType,
          siteId: values.siteId,
          status: values.status
        }
      })
      void message.success(editing === 'new' ? '房间已创建' : '房间已更新')
      await onSaved()
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '房间保存失败'))
      return false
    }
  }

  return (
    <ModalForm<RoomFormValues>
      form={form}
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open={Boolean(editing)}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存房间' },
        submitButtonProps: { loading: upsert.isPending }
      }}
      title={editing === 'new' ? '新建房间' : `编辑房间 · ${editing?.roomNo ?? ''}`}
      width="38rem"
    >
      <Form.Item label="所属场地" name="siteId" rules={[{ message: '请选择场地', required: true }]}>
        <Select
          disabled={siteLocked}
          options={siteOptions}
          placeholder="请选择场地"
          showSearch={{ optionFilterProp: 'label' }}
        />
      </Form.Item>
      <Form.Item label="房间号" name="roomNo" rules={[{ max: 50, message: '请输入房间号', required: true }]}>
        <Input placeholder="例如 A-101" />
      </Form.Item>
      <Form.Item label="楼层" name="floor">
        <Input placeholder="例如 3 楼（选填）" />
      </Form.Item>
      <Form.Item label="房间类型" name="roomType" rules={[{ required: true }]}>
        <Select options={ROOM_TYPE_OPTIONS} />
      </Form.Item>
      <Form.Item label="面积（㎡）" name="area">
        <InputNumber className="w-full" min={0} max={999999.99} placeholder="选填" precision={2} />
      </Form.Item>
      <Form.Item label="状态" name="status" rules={[{ required: true }]}>
        <Select options={statusOptions} />
      </Form.Item>
      <Form.Item label="备注" name="note">
        <Input.TextArea maxLength={2000} placeholder="房间说明（选填）" rows={3} showCount />
      </Form.Item>
    </ModalForm>
  )
}
